import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Make in-page anchors (#events, #platform, ...) work when the URL is
 * pasted/loaded as a deep link.
 *
 * React Router's BrowserRouter doesn't natively scroll to the hash on
 * navigation — and on initial page load the browser tries to scroll to
 * the hash BEFORE the React tree has rendered, so it silently fails.
 *
 * This component watches the hash on every route change and, after a
 * short tick (so lazy images / fonts don't shift the page), smooth-
 * scrolls the matching element into view.
 */
export const HashScrollHandler = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    const RETRY_INTERVAL_MS = 75;
    const MAX_RETRIES = 20;
    // Retry briefly while the section finishes mounting (iframes / images).
    let attempts = 0;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts++ < MAX_RETRIES) {
        setTimeout(tick, RETRY_INTERVAL_MS);
      }
    };
    // Wait one frame so the DOM is ready, then start polling.
    requestAnimationFrame(tick);
  }, [hash]);

  return null;
};

export default HashScrollHandler;
